import React, { useEffect } from 'react'
import { Button, ButtonGroup } from 'reactstrap'
import moment from 'moment'
import { formatEuro } from '../utils'

export const hasValue = (value) => value !== null && value !== undefined

export const formatStatsEuro = (value) => `€ ${formatEuro(value)}`

export const formatStatsPercentValue = (value) => {
  const number = Number(value ?? 0)
  const sign = number > 0 ? '+' : ''
  return `${sign}${number.toFixed(2)} %`
}

export const formatStatsPercentGap = (value) =>
  formatStatsPercentValue(Number(value ?? 0) - 100)

export const getDifferenceColor = (value) => {
  if (value < 20) return '#CA0A0A'
  if (value < 40) return '#E77D1C'
  if (value < 60) return '#F9BC20'
  if (value < 80) return '#8DCA0A'
  return '#04A90A'
}

export const getDifferenceBackground = (value) =>
  value >= 100 ? '#CFF5C9' : '#fff'

export const getDifferenceTone = (value) => {
  const number = Number(value ?? 0)
  if (number >= 100) {
    return {
      color: '#176B34',
      background: '#DFF4E6',
      border: '#9BD8AE',
    }
  }
  if (number >= 80) {
    return {
      color: '#9A4D00',
      background: '#FFF0D9',
      border: '#F2C27C',
    }
  }
  return {
    color: '#A21D16',
    background: '#FCE2DF',
    border: '#E9A39D',
  }
}

export function TotalBadgeCell({
  children,
  value,
  format,
  className = '',
  nowrap = false,
}) {
  const usesValue = Boolean(format) || hasValue(value)
  const content = usesValue
    ? format && hasValue(value)
      ? format(value)
      : value
    : children

  return (
    <td className={`${nowrap ? 'text-nowrap' : ''} ${className}`.trim()}>
      {hasValue(content) && (
        <span
          className={`badge badge-primary ${nowrap ? 'text-nowrap' : ''}`}
          style={{ fontSize: 15 }}
        >
          {content}
        </span>
      )}
    </td>
  )
}

export function HighlightCell({
  active,
  children,
  className = '',
  right = false,
  nowrap = false,
}) {
  return (
    <td
      className={`${nowrap ? 'text-nowrap' : ''} ${
        right ? 'text-right' : ''
      } ${className}`.trim()}
      style={{ background: getDifferenceBackground(active) }}
    >
      {children}
    </td>
  )
}

export function EuroCell({ value, active }) {
  return (
    <HighlightCell active={active} right nowrap>
      {formatStatsEuro(value)}
    </HighlightCell>
  )
}

export function DifferenceCell({ value, active }) {
  const tone = getDifferenceTone(value)

  return (
    <HighlightCell active={active} right nowrap>
      <span
        className="d-inline-block font-weight-bold text-nowrap"
        style={{
          minWidth: 74,
          padding: '2px 8px',
          borderRadius: 4,
          border: `1px solid ${tone.border}`,
          background: tone.background,
          color: tone.color,
          textAlign: 'center',
        }}
      >
        {formatStatsPercentGap(value)}
      </span>
    </HighlightCell>
  )
}

export function MonthlyProgressCell({ stats }) {
  if (!stats) {
    return <td className="text-center text-muted">-</td>
  }

  const progress = Number(stats.differenza_netto_budget ?? 0)

  return (
    <td
      className="text-nowrap"
      style={{
        minWidth: 190,
        background: getDifferenceBackground(progress),
      }}
    >
      <div className="d-flex justify-content-between align-items-center">
        <strong>{formatStatsPercentGap(progress)}</strong>
        <span className="text-muted small">
          {stats.numero_ordini || 0} comm.
        </span>
      </div>
      <div className="small text-muted">
        {formatStatsEuro(stats.netto_serramenti)} /{' '}
        {formatStatsEuro(stats.budget_agente)}
      </div>
      <div
        className="mt-1"
        style={{
          height: 5,
          background: '#D7E7F8',
          position: 'relative',
          borderRadius: 2,
        }}
      >
        <div
          className="position-absolute"
          style={{
            height: 5,
            width: `${progress}%`,
            maxWidth: '100%',
            background: getDifferenceColor(progress),
            borderRadius: 2,
          }}
        />
      </div>
    </td>
  )
}

export function StatsTypeToggle({
  value,
  onChange,
  options = [
    { value: 'table', label: 'Tabella' },
    { value: 'chart', label: 'Grafico' },
  ],
}) {
  return (
    <ButtonGroup className="stats-type-toggle">
      {options.map((option) => (
        <Button
          key={option.value}
          color="primary"
          onClick={() => onChange(option.value)}
          active={value === option.value}
          outline={value !== option.value}
        >
          {option.label}
        </Button>
      ))}
    </ButtonGroup>
  )
}

const MONTHS = moment.months().map((label, index) => ({
  value: index + 1,
  label: label.charAt(0).toUpperCase() + label.slice(1),
}))

const getMonthFilterValues = (filters) => {
  const start = moment(filters.data_inizio, 'YYYY-MM-DD')
  const end = moment(filters.data_fine, 'YYYY-MM-DD')
  const fallback = moment().subtract(1, 'month')
  const safeStart = start.isValid() ? start : fallback
  const safeEnd = end.isValid() ? end : safeStart

  return {
    year: safeStart.year(),
    startMonth: safeStart.month() + 1,
    endMonth:
      safeEnd.year() === safeStart.year()
        ? safeEnd.month() + 1
        : safeStart.month() + 1,
  }
}

export function StatsMonthRangeFilter({ filters, onFiltersChange }) {
  const { year, startMonth, endMonth } = getMonthFilterValues(filters)
  const currentYear = moment().year()
  const years = Array.from({ length: 7 }, (_, index) => currentYear - 4 + index)

  const updateRange = (nextYear, nextStartMonth, nextEndMonth) => {
    const safeEndMonth = Math.max(nextStartMonth, nextEndMonth)
    onFiltersChange({
      data_inizio: moment({
        year: nextYear,
        month: nextStartMonth - 1,
        date: 1,
      }).format('YYYY-MM-DD'),
      data_fine: moment({
        year: nextYear,
        month: safeEndMonth - 1,
        date: 1,
      })
        .endOf('month')
        .format('YYYY-MM-DD'),
    })
  }

  useEffect(() => {
    const start = moment(filters.data_inizio, 'YYYY-MM-DD')
    const end = moment(filters.data_fine, 'YYYY-MM-DD')
    if (start.isValid() && end.isValid() && start.year() !== end.year()) {
      updateRange(start.year(), start.month() + 1, start.month() + 1)
    }
  })

  return (
    <div className="stats-month-range-filter d-flex align-items-center">
      <select
        className="form-control form-control-sm mr-2"
        value={year}
        onChange={(event) =>
          updateRange(Number(event.target.value), startMonth, endMonth)
        }
      >
        {years.map((yearOption) => (
          <option key={yearOption} value={yearOption}>
            {yearOption}
          </option>
        ))}
      </select>
      <select
        className="form-control form-control-sm mr-2"
        value={startMonth}
        onChange={(event) =>
          updateRange(year, Number(event.target.value), endMonth)
        }
      >
        {MONTHS.map((month) => (
          <option key={month.value} value={month.value}>
            Da {month.label}
          </option>
        ))}
      </select>
      <select
        className="form-control form-control-sm"
        value={endMonth}
        onChange={(event) =>
          updateRange(year, startMonth, Number(event.target.value))
        }
      >
        {MONTHS.map((month) => (
          <option
            key={month.value}
            value={month.value}
            disabled={month.value < startMonth}
          >
            A {month.label}
          </option>
        ))}
      </select>
    </div>
  )
}
